import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { BottomNav } from "@/components/BottomNav";
import { MbCard } from "@/components/MbCard";
import { useLanguage } from "@/contexts/LanguageContext";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppFooter } from "@/components/AppFooter";

interface Announcement {
  id: string;
  title: string;
  body: string;
  created_at: string;
}

export default function Notifications() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const { logo, strings } = useLanguage();

  useEffect(() => {
    // Set up auth listener first
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setAuthLoading(false);
        if (!session) {
          navigate("/auth");
        }
      }
    );

    // Then check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setAuthLoading(false);
      if (!session) {
        navigate("/auth");
      } else {
        fetchAnnouncements();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const { data, error } = await supabase
        .from("announcements")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;

      setAnnouncements(data || []);
    } catch (error: any) {
      console.error("Error fetching announcements:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = { 
      day: 'numeric', 
      month: 'long', 
      hour: 'numeric',
      minute: '2-digit'
    };
    return date.toLocaleDateString('gu-IN', options);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-20">
        <div className="animate-pulse text-muted-foreground">{strings.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-[hsl(var(--mb-sky))] pb-20">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <img src={logo} alt="દમયંતી બાગ" className="h-10" />
            <h1 className="text-xl font-bold text-primary">{strings.notifications}</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 space-y-4">
        {announcements.length > 0 ? (
          announcements.map((announcement) => (
            <MbCard key={announcement.id} elevated className="p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                  <Bell className="w-5 h-5 text-accent-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-foreground mb-1">
                    {announcement.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2 whitespace-pre-wrap">
                    {announcement.body}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(announcement.created_at)}
                  </p>
                </div>
              </div>
            </MbCard>
          ))
        ) : (
          <MbCard className="p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
              <Bell className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-bold mb-2">{strings.noNotifications}</h2>
            <p className="text-muted-foreground">{strings.newNotificationsWillAppear}</p>
          </MbCard>
        )}
      </div>

      <div className="pb-16">
        <AppFooter />
      </div>

      <BottomNav />
    </div>
  );
}
